import { Box, Button, Grid, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { LeavePageNotification, TextInput } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';

const declarantSchema = z.object({
  name: z.string().optional(),
  identificationNumber: z.string().optional(),
  address: z.object({
    street: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  }),
  authorisedIdentity: z.string().optional(),
  contactPerson: z.object({
    name: z.string().optional(),
    phoneNumber: z.string().optional(),
    email: z.string().email({ message: 'Invalid email' }).optional(),
  }),
});
interface DeclarantFormProps {
  editDisabled: boolean;
}
type DeclarantFormValues = z.infer<typeof declarantSchema>;

export const DeclarantForm: React.FC<DeclarantFormProps> = ({ editDisabled }) => {
  const { data: settings } = useSettings();
  const { data: declarant, isLoading } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();

  const buildInitialValues = (d) => ({
    name: d?.name || '',
    identificationNumber: d?.identificationNumber || '',
    address: {
      street: d?.address?.street || '',
      postcode: d?.address?.postcode || '',
      city: d?.address?.city || '',
      country: d?.address?.country || '',
    },
    authorisedIdentity: d?.authorisedIdentity || '',
    contactPerson: {
      name: d?.contactPerson?.name || '',
      phoneNumber: d?.contactPerson?.phoneNumber || '',
      email: d?.contactPerson?.email || '',
    },
  });

  const form = useFormWithNameInputProp<DeclarantFormValues>({
    initialValues: buildInitialValues(declarant),
    validateInputOnBlur: true,
    validate: zodResolver(declarantSchema),
  });

  const handleSubmit = (values: DeclarantFormValues) => {
    if (declarant) {
      updateDeclarantMutation.mutate({
        declarantId: declarant._id,
        declarant: values,
      });
    }
  };

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({});
  }, [declarant]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Box>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Box>
            <Grid>
              <Grid.Col span={8}>
                <TextInput {...form.getInputProps('name')} label="Name" data-pw="declarant-name-input" />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('identificationNumber')} label="Identification number" data-pw="declarant-id-input" />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('address.street')} label="Address" data-pw="declarant-address-input" />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('address.postcode')} label="Postcode" data-pw="declarant-postcode-input" />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('address.city')} label="City" data-pw="declarant-city-input" />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('address.country')} label="Country" data-pw="declarant-country-input" />
              </Grid.Col>
            </Grid>

            {!settings?.isForwarder && (
              <Grid mt="md">
                <Grid.Col span={8}>
                  <TextInput {...form.getInputProps('authorisedIdentity')} label="Authorised identity" data-pw="declarant-auth-input" />
                </Grid.Col>
              </Grid>
            )}
          </Box>

          {!settings?.isForwarder && (
            <Box>
              <Title order={4} fw={600} mb="md">
                Contact person
              </Title>
              <Grid>
                <Grid.Col span={4}>
                  <TextInput {...form.getInputProps('contactPerson.name')} label="Name" data-pw="contact-name-input" />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput {...form.getInputProps('contactPerson.phoneNumber')} label="Phone number" data-pw="contact-phone-input" />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput {...form.getInputProps('contactPerson.email')} label="E-mail" data-pw="contact-email-input" />
                </Grid.Col>
              </Grid>
            </Box>
          )}

          <Group py="xl">
            <Button
              type="submit"
              variant="filled"
              size="lg"
              loading={updateDeclarantMutation.isPending}
              disabled={editDisabled || !form.isValid()}
              data-pw="save-declarant-button"
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};
